import React from 'react';

import { useUserContext } from '@/components/UserContext';

import {
    PagesTableColumnKey,
    TasksTableColumnKey,
    CollectionsTableColumnKey,
    CollectionPropertyOrder,
    CollectionTableColumnKey,
    CollectionWithProperties,
} from '@saga/shared';
import { SharedPagesTableColumnKey } from '@saga/shared';

import * as api from '@saga/api';
import { apolloClient } from '@/graphql';
const DEFAULT_COLUMNS: PagesTableColumnKey[] = ['title', 'collection', 'wordCount', 'createdBy', 'created', 'updated'];
const DEFAULT_SHARED_COLUMNS: SharedPagesTableColumnKey[] = ['title', 'sharedBy', 'created', 'role'];
const DEFAULT_TASKS_COLUMNS: TasksTableColumnKey[] = [
    'title',
    'collection',
    'assignee',
    'dueDate',
    'priority',
    'references',
    'labels',
    'createdBy',
    'created',
    'updated',
    'completedDate',
];
const DEFAULT_COLLECTIONS_COLUMNS: CollectionsTableColumnKey[] = ['title', 'pageCount', 'created', 'updated'];
const DEFAULT_COLLECTION_FOLDER_COLUMNS: CollectionTableColumnKey[] = ['title', 'created', 'updated'];
const DEFAULT_COLLECTION_TAG_COLUMNS: CollectionTableColumnKey[] = ['title', 'subCollections', 'created', 'updated'];

export const usePagesTableColumns = (): [PagesTableColumnKey[], (columns: PagesTableColumnKey[]) => void] => {
    const { userSettings } = useUserContext();
    const [updateUserSettings] = api.useUpdateUserSettingsMutation();

    const columns = React.useMemo(() => {
        return (userSettings?.extendedSettings?.pagesColumnsOrder as PagesTableColumnKey[]) ?? DEFAULT_COLUMNS;
    }, [userSettings]);

    const setColumns = React.useCallback(
        (columns: PagesTableColumnKey[]) => {
            updateUserSettings({
                variables: {
                    input: { extendedSettings: { pagesColumnsOrder: columns } },
                },
                update(cache, { data }) {
                    if (!data?.updateUserSettings) return;

                    const cachedData = cache.readQuery<api.UserDataQuery>({
                        query: api.UserDataDocument,
                    });

                    cache.writeQuery({
                        query: api.UserDataDocument,
                        data: {
                            ...(cachedData ?? {}),
                            userSettings: {
                                ...(cachedData?.userSettings ?? {}),
                                extendedSettings: {
                                    ...(cachedData?.userSettings?.extendedSettings ?? {}),
                                    pagesColumnsOrder: columns,
                                },
                            },
                        },
                    });
                },
            });
        },
        [updateUserSettings],
    );

    return React.useMemo(() => [columns, setColumns], [columns, setColumns]);
};

export const useSharedPagesTableColumns = (): [
    SharedPagesTableColumnKey[],
    (columns: SharedPagesTableColumnKey[]) => void,
] => {
    const { userSettings } = useUserContext();
    const [updateUserSettings] = api.useUpdateUserSettingsMutation();

    const columns = React.useMemo(() => {
        return (
            (userSettings?.extendedSettings?.sharedPagesColumnsOrder as SharedPagesTableColumnKey[]) ??
            DEFAULT_SHARED_COLUMNS
        );
    }, [userSettings]);

    const setColumns = React.useCallback(
        (columns: SharedPagesTableColumnKey[]) => {
            updateUserSettings({
                variables: {
                    input: { extendedSettings: { sharedPagesColumnsOrder: columns } },
                },
                update(cache, { data }) {
                    if (!data?.updateUserSettings) return;

                    const cachedData = cache.readQuery<api.UserDataQuery>({
                        query: api.UserDataDocument,
                    });

                    cache.writeQuery({
                        query: api.UserDataDocument,
                        data: {
                            ...(cachedData ?? {}),
                            userSettings: {
                                ...(cachedData?.userSettings ?? {}),
                                extendedSettings: {
                                    ...(cachedData?.userSettings?.extendedSettings ?? {}),
                                    sharedPagesColumnsOrder: columns,
                                },
                            },
                        },
                    });
                },
            });
        },
        [updateUserSettings],
    );

    return React.useMemo(() => [columns, setColumns], [columns, setColumns]);
};

export const useTaskTableColumns = (): [TasksTableColumnKey[], (columns: TasksTableColumnKey[]) => void] => {
    const { userSettings } = useUserContext();
    const [updateUserSettings] = api.useUpdateUserSettingsMutation();

    const columns = React.useMemo(() => {
        return (userSettings?.extendedSettings?.tasksColumnsOrder as TasksTableColumnKey[]) ?? DEFAULT_TASKS_COLUMNS;
    }, [userSettings]);

    const setColumns = React.useCallback(
        (columns: TasksTableColumnKey[]) => {
            updateUserSettings({
                variables: {
                    input: { extendedSettings: { tasksColumnsOrder: columns } },
                },
                update(cache, { data }) {
                    if (!data?.updateUserSettings) return;

                    const cachedData = cache.readQuery<api.UserDataQuery>({
                        query: api.UserDataDocument,
                    });

                    cache.writeQuery({
                        query: api.UserDataDocument,
                        data: {
                            ...(cachedData ?? {}),
                            userSettings: {
                                ...(cachedData?.userSettings ?? {}),
                                extendedSettings: {
                                    ...(cachedData?.userSettings?.extendedSettings ?? {}),
                                    tasksColumnsOrder: columns,
                                },
                            },
                        },
                    });
                },
            });
        },
        [updateUserSettings],
    );

    return React.useMemo(() => [columns, setColumns], [columns, setColumns]);
};

export const useCollectionsTableColumns = (): [
    CollectionsTableColumnKey[],
    (columns: CollectionsTableColumnKey[]) => void,
] => {
    const { userSettings } = useUserContext();
    const [updateUserSettings] = api.useUpdateUserSettingsMutation();

    const columns = React.useMemo(() => {
        return (
            (userSettings?.extendedSettings?.collectionsColumnsOrder as CollectionsTableColumnKey[]) ??
            DEFAULT_COLLECTIONS_COLUMNS
        );
    }, [userSettings]);

    const setColumns = React.useCallback(
        (columns: CollectionsTableColumnKey[]) => {
            updateUserSettings({
                variables: {
                    input: { extendedSettings: { collectionsColumnsOrder: columns } },
                },
                update(cache, { data }) {
                    if (!data?.updateUserSettings) return;

                    const cachedData = cache.readQuery<api.UserDataQuery>({
                        query: api.UserDataDocument,
                    });

                    cache.writeQuery({
                        query: api.UserDataDocument,
                        data: {
                            ...(cachedData ?? {}),
                            userSettings: {
                                ...(cachedData?.userSettings ?? {}),
                                extendedSettings: {
                                    ...(cachedData?.userSettings?.extendedSettings ?? {}),
                                    collectionsColumnsOrder: columns,
                                },
                            },
                        },
                    });
                },
            });
        },
        [updateUserSettings],
    );

    return React.useMemo(() => [columns, setColumns], [columns, setColumns]);
};

export const useCollectionPropertiesOrder = (
    collection: Pick<CollectionWithProperties, 'id' | 'extendedProperties'>,
): [
    Omit<CollectionPropertyOrder, 'collectionId'>,
    (order: Omit<CollectionPropertyOrder, 'collectionId'>, options: { localOnly?: boolean }) => void,
] => {
    const { userSettings } = useUserContext();
    const [updateUserSettings] = api.useUpdateUserSettingsMutation();

    const order = React.useMemo(() => {
        const collectionOrder = userSettings?.extendedSettings?.collectionPropertiesOrder?.find(
            (order) => order.collectionId === collection.id,
        );

        const tagViewProperties = [
            ...DEFAULT_COLLECTION_TAG_COLUMNS,
            ...collection.extendedProperties.map((p) => p.id),
        ];
        const folderViewProperties = [
            ...DEFAULT_COLLECTION_FOLDER_COLUMNS,
            ...collection.extendedProperties.map((p) => p.id),
        ];

        tagViewProperties.sort((a, b) => {
            const orderA = collectionOrder?.tagViewProperties.indexOf(a) ?? -1;
            const orderB = collectionOrder?.tagViewProperties.indexOf(b) ?? -1;
            return (
                (orderA === -1 ? Number.MAX_SAFE_INTEGER : orderA) - (orderB === -1 ? Number.MAX_SAFE_INTEGER : orderB)
            );
        });

        folderViewProperties.sort((a, b) => {
            const orderA = collectionOrder?.folderViewProperties.indexOf(a) ?? -1;
            const orderB = collectionOrder?.folderViewProperties.indexOf(b) ?? -1;
            return (
                (orderA === -1 ? Number.MAX_SAFE_INTEGER : orderA) - (orderB === -1 ? Number.MAX_SAFE_INTEGER : orderB)
            );
        });

        return {
            tagViewProperties,
            folderViewProperties,
        };
    }, [userSettings, collection.id, collection.extendedProperties]);

    const setOrder = React.useCallback(
        (newOrder: Omit<CollectionPropertyOrder, 'collectionId'>, options: { localOnly?: boolean } = {}) => {
            const writeToCache = () => {
                const cachedData = apolloClient.cache.readQuery<api.UserDataQuery>({
                    query: api.UserDataDocument,
                });

                apolloClient.writeQuery({
                    query: api.UserDataDocument,
                    data: {
                        ...(cachedData ?? {}),
                        userSettings: {
                            ...(cachedData?.userSettings ?? {}),
                            extendedSettings: {
                                ...(cachedData?.userSettings?.extendedSettings ?? {}),
                                collectionPropertiesOrder: [
                                    ...(
                                        cachedData?.userSettings?.extendedSettings?.collectionPropertiesOrder ?? []
                                    ).filter((order) => order.collectionId !== collection.id),
                                    {
                                        collectionId: collection.id,
                                        ...newOrder,
                                    },
                                ],
                            },
                        },
                    },
                });
            };

            if (options.localOnly) {
                writeToCache();
            } else {
                updateUserSettings({
                    variables: {
                        input: {
                            extendedSettings: {
                                collectionPropertiesOrder: [
                                    ...(userSettings?.extendedSettings?.collectionPropertiesOrder ?? [])
                                        .filter((order) => order.collectionId !== collection.id)
                                        .map((order) => ({
                                            collectionId: order.collectionId,
                                            tagViewProperties: order.tagViewProperties,
                                            folderViewProperties: order.folderViewProperties,
                                        })),
                                    {
                                        collectionId: collection.id,
                                        ...newOrder,
                                    },
                                ],
                            },
                        },
                    },
                    update(_, { data }) {
                        if (!data?.updateUserSettings) return;
                        writeToCache();
                    },
                });
            }
        },
        [updateUserSettings, collection.id, userSettings],
    );

    return React.useMemo(() => [order, setOrder], [order, setOrder]);
};
